<template>
  <SubLayout
      :pageTitle="title"
      :pageBreadcrumbs="breadcrumbs"
      :loading="isLoading"
  >
    <template #layoutContent>
      <AppointmentForm
          :appointment="appointment"
          :submit-form="route.params.id ? updateAppointment : createAppointment"
          :submitting="submitting"
      />
    </template>
  </SubLayout>
</template>

<script setup>

import SubLayout from "@/views/Layouts/partials/SubLayout.vue";
import {useRoute, useRouter} from "vue-router";
import {useStore} from "vuex";
import {computed, onMounted, ref, watch} from "vue";
import AppointmentForm from "@/Modules/CRM/components/Appointment/AppointmentForm.vue";
import {useSwal} from "@/composables/useSwal";
import i18n from "@/plugins/i18n";
import { parse } from 'date-fns';

const route = useRoute()
const router = useRouter()
const store = useStore()
const {showSwal} = useSwal()

const title = ref("appointments.title")
const breadcrumbs = ref([
  {
    name: 'appointments.list',
    route: 'appointments',
    params: {}
  },
  {
    name: route.params.id ? 'appointments.edit' : 'appointments.new',
    route: route.params.id ? 'appointments-edit' : 'appointments-create',
    params: route.params.id ? { id: route.params.id } : null
  }
])

const isLoading = ref(true)
const submitting = ref(false)


const appointment = ref({
  id: null,
  title: '',
  appointment_type_id: '',
  appointment_room_id: '',
  start_time: '',
  end_time: '',
  description: '',
  location: '',
  guest_emails: [],
  contacts: [],
  users: [],
  link: '',
  is_recurring: false,
  frequency_appointment: '',
  frequency_count_appointment: null,
  recurring_date: null,
  frequency: '',
  limit_by: 'none',
  limit_count: null,
  frequency_count: null,
  limit_date: null,
  sent_email: false,
  send_location: false,
  model_ids: []

})

const currentAppointment = computed(() => store.getters['appointments/currentAppointment'])

watch(() =>currentAppointment.value, (newValue) => {
  if(route.params.id) {
    appointment.value.title = newValue.title
    appointment.value.appointment_type_id = newValue.appointment_type_id
    appointment.value.appointment_room_id = newValue.appointment_room_id
    appointment.value.start_time = newValue.start_time
    appointment.value.end_time = newValue.end_time
    appointment.value.id = newValue.id
    appointment.value.description = newValue.description || ''
    appointment.value.location = newValue.location
    appointment.value.guest_emails = newValue.guests?.map(item => item.email)
    appointment.value.contacts = newValue.customers?.map(item => item.id)
    appointment.value.users = newValue.collaborators?.map(item => item.id)
    appointment.value.link = newValue.link
    appointment.value.frequency_appointment = newValue.frequency_appointment,
    appointment.value.frequency_count_appointment = newValue.frequency_count_appointment,
    appointment.value.recurring_date = newValue.recurring?.recurring_date || null,
    appointment.value.frequency = newValue.recurring?.frequency || null,
    appointment.value.limit_by = newValue.recurring?.limit_by || 'none',
    appointment.value.limit_count = newValue.recurring?.limit_count || null,
    appointment.value.frequency_count = newValue.recurring?.frequency_count || null,
    appointment.value.limit_date = newValue.recurring?.limit_date ? moment(new Date(newValue.recurring.limit_date)).format('DD-MM-YYYY') : null,
    appointment.value.is_recurring = newValue.is_recurring
    appointment.value.send_location = newValue.send_location

  } else {

    appointment.value = null
    appointment.value = {
      id: null,
      title: '',
      appointment_type_id: '',
      appointment_room_id: '',
      start_time: '',
      end_time: '',
      description: '',
      location: '',
      guest_emails: [],
      contacts: [],
      users: [],
      link: '',
      is_recurring: false,
      frequency_appointment: '',
      frequency_count_appointment: null,
      recurring_date: null,
      frequency: '',
      limit_by: 'none',
      limit_count: null,
      frequency_count: null,
      limit_date: null,
      sent_email: false,
      send_location: false
    }
  }

}, {deep: true})
const createAppointment = () => {
  const today = new Date();
  const startDate = parse(appointment.value.start_time, 'dd-MM-yyyy HH:mm', new Date()); 

  submitting.value = true
  if(appointment.value.is_recurring){
    appointment.value.recurring_date = appointment.value.start_time?.split(' ')[0]
  }
  if(startDate > today && (appointment.value.contacts.length > 0 || appointment.value.users.length > 0 || appointment.value.guest_emails.length > 0)){
    showSwal(
          '',
        i18n.global.t('appointments.swal.create_update'),
        'success',
        '#2DA333'
    ).then( async (result) => {
        if (result.value) {
            try {
              appointment.value.sent_email = true
              store.dispatch('appointments/createAppointment', appointment.value)
                  .then((response) => {
                    window.toastr['success'](i18n.global.t('notification.appointment_created'))
                    router.push({ name: 'appointments-view', params: { id: response.data.data?.id } })
                  })
                  .catch(err => {
                  }).finally(() => submitting.value = false)
            }catch (e) {
              
            }
        }else{
          appointment.value.sent_email = false
              store.dispatch('appointments/createAppointment', appointment.value)
                  .then((response) => {
                    window.toastr['success'](i18n.global.t('notification.appointment_created'))
                    router.push({ name: 'appointments-view', params: { id: response.data.data?.id } })
                  })
                  .catch(err => {
                  }).finally(() => submitting.value = false)
              }
          });
  }else{
    appointment.value.sent_email = false
    store.dispatch('appointments/createAppointment', appointment.value)
    .then((response) => {
      window.toastr['success'](i18n.global.t('notification.appointment_created'))
      router.push({ name: 'appointments-view', params: { id: response.data.data?.id } })
    })
    .catch(err => {
    }).finally(() => submitting.value = false)
  }
}

const updateAppointment = () => {
  submitting.value = true
  if(appointment.value.is_recurring){
    appointment.value.recurring_date = appointment.value.start_time.split(' ')[0]
  }
  if(appointment.value.contacts.length > 0 || appointment.value.users.length > 0 || appointment.value.guest_emails.length > 0){
    showSwal(
          '',
        i18n.global.t('appointments.swal.create_update'),
        'success',
        '#2DA333'
    ).then( async (result) => {
        if (result.value) {
            try {
              appointment.value.sent_email = true
              store.dispatch('appointments/updateAppointment', appointment.value)
                .then((response) => {
                  window.toastr['success'](i18n.global.t('notification.appointment_updated'))
                  router.push({ name: 'appointments-view', params: { id: response.data.data?.id } })
                })
                .catch(err => {
                }).finally(() => submitting.value = false)
            }catch (e) {
              
            }
        }else{
          store.dispatch('appointments/updateAppointment', appointment.value)
              .then((response) => {
                window.toastr['success'](i18n.global.t('notification.appointment_updated'))
                router.push({ name: 'appointments-view', params: { id: response.data.data?.id } })
              })
              .catch(err => {
              }).finally(() => submitting.value = false)
        }
    });
  }else{
    store.dispatch('appointments/updateAppointment', appointment.value)
        .then((response) => {
          window.toastr['success'](i18n.global.t('notification.appointment_updated'))
          router.push({ name: 'appointments-view', params: { id: response.data.data?.id } })
        })
        .catch(err => {
        }).finally(() => submitting.value = false)
  }
}

const loadcurrentAppointment = async () => {
  if(route.params.id){
    store.dispatch('appointments/getAppointmentById', route.params.id)
  }
}

function loadData() {
  Promise.all([
      loadcurrentAppointment()
  ]).finally(() => {
    isLoading.value = false
  })
}

onMounted(() => {
  loadData()
})

</script>

<style scoped>

</style>